import { forwardRef } from 'react'
import { Link, useLocation, useNavigate, type LinkProps, type NavigateOptions } from 'react-router-dom'
import { buildLocalizedPath, isLanguageNeutralPath, languageFromPathname } from '@/lib/i18n'

/**
 * NAVEGAÇÃO INTERNA QUE PRESERVA O IDIOMA (D2 do PLANO-I18N).
 *
 * O problema que este módulo fecha: em `/ja`, um `<Link to="/mapa">` literal
 * levava para a versão PORTUGUESA da página. Pior: o `LocaleGate` então
 * gravava `pt` por cima da preferência do usuário — o japonês perdia o idioma
 * de forma permanente ao clicar em qualquer CTA ou tópico do fórum.
 *
 * A classe do problema não era "faltou prefixar 10 links": era não existir
 * barreira nenhuma contra o 11º. Por isso a superfície pública passa a usar
 * SEMPRE `LocalizedLink`/`useLocalizedNavigate`, e o teste
 * `src/__tests__/localized-links.test.ts` varre esses arquivos e falha se um
 * `Link`/`navigate` com path literal reaparecer.
 *
 * O idioma é lido da URL, não do `LanguageContext`: a URL é a autoridade única
 * (D3), e assim estes helpers funcionam também fora do `LanguageProvider`
 * (`NotFoundPage` é renderizada pelo `LocaleGate`, antes do layout público).
 *
 * Paths NEUTROS de idioma (`/login`, `/app/*`, `/trace/*`) passam intactos:
 * eles não têm equivalente prefixado, e prefixá-los daria 404.
 */

function localizePath(to: string, currentPathname: string): string {
  // Âncora, query pura, URL absoluta ou path relativo: não é rota interna
  // absoluta — nada a prefixar.
  if (!to.startsWith('/')) return to
  if (isLanguageNeutralPath(to)) return to

  const [pathAndSearch, ...hashParts] = to.split('#')
  const hash = hashParts.length > 0 ? `#${hashParts.join('#')}` : ''
  const [path, ...searchParts] = pathAndSearch.split('?')
  const search = searchParts.length > 0 ? `?${searchParts.join('?')}` : ''

  return buildLocalizedPath(path, languageFromPathname(currentPathname), search, hash)
}

/**
 * `localize('/mapa')` -> `/ja/mapa` em `/ja/...`, `/mapa` em PT.
 * Exposto para os poucos casos em que o destino não é um `<Link>`
 * (ex.: `<Button asChild>` com `to` calculado, listas de navegação).
 */
export function useLocalizedPath(): (to: string) => string {
  const { pathname } = useLocation()
  return (to: string) => localizePath(to, pathname)
}

/** `navigate()` que preserva o idioma corrente. */
export function useLocalizedNavigate(): (to: string, options?: NavigateOptions) => void {
  const navigate = useNavigate()
  const localize = useLocalizedPath()
  return (to: string, options?: NavigateOptions) => navigate(localize(to), options)
}

/**
 * `<Link>` do react-router com o prefixo de idioma aplicado ao `to`.
 * API idêntica à do `Link` — só aceita `to` string (que é como a superfície
 * pública o usa); um objeto `To` passaria sem localizar e mascararia o bug.
 */
export const LocalizedLink = forwardRef<HTMLAnchorElement, Omit<LinkProps, 'to'> & { to: string }>(
  function LocalizedLink({ to, ...rest }, ref) {
    const localize = useLocalizedPath()
    return <Link ref={ref} to={localize(to)} {...rest} />
  },
)
